/**
 * Transforms raw API report data into Chart.js and table formats for ReportChart.
 * Pure functions; no side effects.
 *
 * @param {Array} rawData - Array of data points, e.g. [{ month, revenue, orders, avg_revenue }, ...]
 * @param {Object} options - { metrics: [{ key, label, color, yAxisID, format }] }
 * @returns {{ chartData: Object, tableData: Object }}
 */
export function parseReportData(rawData, options = {}) {
  const metrics = options.metrics || []
  const labels = rawData.map(d => d.month || d.label || d.period)
  const columns = labels.map((l, i) => ({
    key: (l || '').toLowerCase().replace(/\s/g, '_') || `col_${i}`,
    label: (l || '').toUpperCase()
  }))

  const datasets = metrics.map(m => {
    const key = m.key
    const data = rawData.map(d => d[key] ?? 0)
    const dataset = {
      label: m.label,
      data,
      borderColor: m.color,
      backgroundColor: m.backgroundColor ?? (m.color ? `${m.color}14` : 'rgba(0,0,0,0.08)'),
      yAxisID: m.yAxisID ?? 'y',
      tension: m.tension ?? 0.4,
      fill: m.fill !== false,
      borderWidth: m.borderWidth ?? 1.5
    }
    if (m.borderDash) dataset.borderDash = m.borderDash
    return dataset
  })

  const chartData = {
    labels,
    datasets
  }

  const formatValue = (metric, value, allowZero = false) => {
    if (value == null) return '—'
    if (!allowZero && Number(value) === 0) return '—'
    const fmt = metric.format
    if (fmt === 'currency') return `$${Number(value).toLocaleString()}`
    if (fmt === 'number') return Number(value).toLocaleString()
    return String(value)
  }

  const rows = metrics.map(m => {
    const key = m.key
    const values = {}
    let total = 0
    rawData.forEach((d, i) => {
      const v = d[key] ?? 0
      const colKey = columns[i]?.key
      if (colKey) values[colKey] = formatValue(m, v)
      if (typeof v === 'number') total += v
    })
    return {
      label: m.tableLabel ?? m.label,
      color: m.color,
      values,
      total: formatValue(m, total, true)
    }
  })

  const tableData = {
    columns,
    rows
  }

  return { chartData, tableData }
}

/**
 * Aggregates payout list by period (day/week/month) and returns chart + table data for ReportChart.
 * @param {Array} rawPayouts - Array of { arrival_date (Unix), amount_dollars } (or amount in cents if no amount_dollars)
 * @param {string} groupBy - 'day' | 'week' | 'month' | 'year'
 * @returns {{ chartData: Object, tableData: Object }}
 */
export function parsePayoutsReport(rawPayouts, groupBy = 'day') {
  if (!rawPayouts || !Array.isArray(rawPayouts) || rawPayouts.length === 0) {
    return {
      chartData: { labels: [], datasets: [] },
      tableData: { columns: [], rows: [] }
    }
  }

  const getPeriodKey = ts => {
    const d = new Date(ts * 1000)
    const y = d.getFullYear()
    const m = d.getMonth()
    const date = d.getDate()
    if (groupBy === 'year') return `${y}`
    if (groupBy === 'month') return `${y}-${String(m + 1).padStart(2, '0')}`
    if (groupBy === 'week') {
      const day = d.getDay()
      const start = new Date(d)
      start.setDate(date - day)
      const sy = start.getFullYear()
      const sm = start.getMonth() + 1
      const sd = start.getDate()
      return `${sy}-${String(sm).padStart(2, '0')}-${String(sd).padStart(2, '0')}`
    }
    return `${y}-${String(m + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`
  }

  const periodToLabel = key => {
    if (groupBy === 'year') return key
    const [y, m, d] = key.split('-').map(Number)
    if (groupBy === 'month') {
      const date = new Date(y, m - 1, 1)
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    }
    if (groupBy === 'week') {
      const date = new Date(y, m - 1, d)
      const end = new Date(date)
      end.setDate(end.getDate() + 6)
      return `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    }
    const date = new Date(y, m - 1, d)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const buckets = new Map()
  for (const p of rawPayouts) {
    const key = getPeriodKey(p.arrival_date)
    const amount = p.amount_dollars != null ? p.amount_dollars : p.amount / 100
    buckets.set(key, (buckets.get(key) || 0) + amount)
  }

  const sortedKeys = [...buckets.keys()].sort()
  const labels = sortedKeys.map(periodToLabel)
  const values = sortedKeys.map(k => buckets.get(k))
  const total = values.reduce((s, v) => s + v, 0)

  const colKey = i => (labels[i] || '').toLowerCase().replace(/\s/g, '_').replace(/,/g, '') || `col_${i}`
  const columns = labels.map((l, i) => ({ key: colKey(i), label: l || `Period ${i + 1}` }))

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Payouts',
        data: values,
        borderColor: 'rgb(71, 114, 196)',
        backgroundColor: 'rgba(71, 114, 196, 0.08)',
        yAxisID: 'y',
        tension: 0.4,
        fill: true,
        borderWidth: 1.5
      }
    ]
  }

  const formatCurrency = v =>
    v == null || Number(v) === 0
      ? '—'
      : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  const formatCurrencyTotal = v =>
    v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  const rowValues = {}
  columns.forEach((col, i) => {
    rowValues[col.key] = formatCurrency(values[i])
  })

  const tableData = {
    columns,
    rows: [
      {
        label: 'Payouts',
        color: 'rgb(71, 114, 196)',
        values: rowValues,
        total: formatCurrencyTotal(total)
      }
    ]
  }

  return { chartData, tableData }
}

export const REVENUE_SERIES = {
  gross: { label: 'Gross Revenue', color: '71, 114, 196' },
  refunds: { label: 'Refunds', color: '205, 92, 92' },
  net: { label: 'Net Revenue', color: '75, 163, 120' }
}

const revenueAttr = (row, key) => row?.attributes?.[key] ?? row?.[key]

const padDatePart = value => String(value).padStart(2, '0')

export function revenuePeriodKey(dateStr, groupBy) {
  if (!dateStr) return null
  const date = new Date(dateStr + 'T12:00:00')
  if (isNaN(date.getTime())) return null
  const year = date.getFullYear()
  const month = padDatePart(date.getMonth() + 1)
  if (groupBy === 'year') return `${year}`
  if (groupBy === 'month') return `${year}-${month}`
  if (groupBy === 'week') {
    const start = new Date(date)
    start.setDate(date.getDate() - date.getDay())
    return `${start.getFullYear()}-${padDatePart(start.getMonth() + 1)}-${padDatePart(start.getDate())}`
  }
  return `${year}-${month}-${padDatePart(date.getDate())}`
}

export function revenuePeriodLabel(key, groupBy) {
  if (groupBy === 'year') return key
  const [year, month, day] = key.split('-').map(Number)
  if (groupBy === 'month') {
    return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  }
  const date = new Date(year, month - 1, day)
  const short = value => value.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  if (groupBy === 'week') {
    const end = new Date(date)
    end.setDate(end.getDate() + 6)
    return `${short(date)} – ${short(end)}`
  }
  return short(date)
}

export function bucketRevenueRows(rawRows, groupBy = 'day') {
  if (!Array.isArray(rawRows)) return []
  const buckets = new Map()
  rawRows.forEach(row => {
    const key = revenuePeriodKey(revenueAttr(row, 'day'), groupBy)
    if (key === null) return
    const bucket = buckets.get(key) ?? {
      key,
      label: revenuePeriodLabel(key, groupBy),
      gross: 0,
      refunds: 0,
      net: 0,
      orders: 0
    }
    bucket.gross += Number(revenueAttr(row, 'gross_revenue')) || 0
    bucket.refunds += Number(revenueAttr(row, 'refunds')) || 0
    bucket.net += Number(revenueAttr(row, 'net_revenue')) || 0
    bucket.orders += Number(revenueAttr(row, 'orders')) || 0
    buckets.set(key, bucket)
  })
  return [...buckets.values()].sort((a, b) => (a.key < b.key ? -1 : 1))
}

const revenueDataset = (series, periods) => ({
  label: REVENUE_SERIES[series].label,
  data: periods.map(period => period[series]),
  borderColor: `rgb(${REVENUE_SERIES[series].color})`,
  backgroundColor: `rgba(${REVENUE_SERIES[series].color}, 0.08)`,
  yAxisID: 'y',
  tension: 0.4,
  fill: true,
  borderWidth: 1.5
})

export function revenueChartData(periods, series = ['gross', 'refunds', 'net']) {
  return {
    labels: periods.map(period => period.label),
    datasets: series.map(name => revenueDataset(name, periods))
  }
}

/**
 * Aggregates daily revenue rows by period (day/month/year) and returns chart + table data.
 * Input: JSONAPI resources with attributes.day (date string), .gross_revenue, .refunds, .net_revenue.
 * @param {Array} rawRows - Array of JSONAPI resources: { attributes: { day, gross_revenue, refunds, net_revenue } }
 * @param {string} groupBy - 'day' | 'week' | 'month' | 'year'
 * @returns {{ chartData: Object, tableData: Object }}
 */
export function parseRevenueReport(rawRows, groupBy = 'day') {
  if (!rawRows || !Array.isArray(rawRows) || rawRows.length === 0) {
    return {
      chartData: { labels: [], datasets: [] },
      tableData: { columns: [], rows: [] }
    }
  }

  const periods = bucketRevenueRows(rawRows, groupBy)
  const labels = periods.map(period => period.label)
  const grossValues = periods.map(period => period.gross)
  const refundValues = periods.map(period => period.refunds)
  const netValues = periods.map(period => period.net)

  const colKey = i => (labels[i] || '').toLowerCase().replace(/\s/g, '_').replace(/,/g, '') || `col_${i}`
  const columns = labels.map((l, i) => ({ key: colKey(i), label: l || `Period ${i + 1}` }))

  const formatCurrency = v =>
    v == null || Number(v) === 0
      ? '—'
      : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  const formatCurrencyTotal = v =>
    v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  const chartData = revenueChartData(periods)

  const grossRowValues = {}
  const refundRowValues = {}
  const netRowValues = {}
  columns.forEach((col, i) => {
    grossRowValues[col.key] = formatCurrency(grossValues[i])
    refundRowValues[col.key] = formatCurrency(refundValues[i])
    netRowValues[col.key] = formatCurrency(netValues[i])
  })

  const tableData = {
    columns,
    hideTotalRow: true,
    rows: [
      {
        label: 'Gross Revenue',
        color: 'rgb(71, 114, 196)',
        values: grossRowValues,
        total: formatCurrencyTotal(grossValues.reduce((s, v) => s + v, 0))
      },
      {
        label: 'Refunds',
        color: 'rgb(205, 92, 92)',
        values: refundRowValues,
        total: formatCurrencyTotal(refundValues.reduce((s, v) => s + v, 0))
      },
      {
        label: 'Net Revenue',
        color: 'rgb(75, 163, 120)',
        values: netRowValues,
        total: formatCurrencyTotal(netValues.reduce((s, v) => s + v, 0))
      }
    ]
  }

  return { chartData, tableData }
}

/**
 * Aggregates recurring membership fee orders by period (day/week/month) and returns chart + table data.
 * @param {Array} rawRows - Array of { fulfillment_date (Unix), recurring_fee_cents }
 * @param {string} groupBy - 'day' | 'week' | 'month' | 'year'
 * @returns {{ chartData: Object, tableData: Object }}
 */
export function parseRecurringMembershipFeesReport(rawRows, groupBy = 'day') {
  if (!rawRows || !Array.isArray(rawRows) || rawRows.length === 0) {
    return {
      chartData: { labels: [], datasets: [] },
      tableData: { columns: [], rows: [] }
    }
  }

  const getPeriodKey = ts => {
    const d = new Date(ts * 1000)
    const y = d.getFullYear()
    const m = d.getMonth()
    const date = d.getDate()
    if (groupBy === 'year') return `${y}`
    if (groupBy === 'month') return `${y}-${String(m + 1).padStart(2, '0')}`
    if (groupBy === 'week') {
      const day = d.getDay()
      const start = new Date(d)
      start.setDate(date - day)
      const sy = start.getFullYear()
      const sm = start.getMonth() + 1
      const sd = start.getDate()
      return `${sy}-${String(sm).padStart(2, '0')}-${String(sd).padStart(2, '0')}`
    }
    return `${y}-${String(m + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`
  }

  const periodToLabel = key => {
    if (groupBy === 'year') return key
    const parts = key.split('-').map(Number)
    const y = parts[0]
    const m = parts[1]
    const d = parts[2] ?? 1
    if (groupBy === 'month') {
      const date = new Date(y, m - 1, 1)
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    }
    if (groupBy === 'week') {
      const date = new Date(y, m - 1, d)
      const end = new Date(date)
      end.setDate(end.getDate() + 6)
      return `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    }
    const date = new Date(y, m - 1, d)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const countBuckets = new Map()
  const feeBuckets = new Map()
  for (const row of rawRows) {
    const key = getPeriodKey(row.fulfillment_date)
    countBuckets.set(key, (countBuckets.get(key) || 0) + 1)
    const feeDollars = (row.recurring_fee_cents || 0) / 100
    feeBuckets.set(key, (feeBuckets.get(key) || 0) + feeDollars)
  }

  const sortedKeys = [...countBuckets.keys()].sort()
  const labels = sortedKeys.map(periodToLabel)
  const orderCounts = sortedKeys.map(k => countBuckets.get(k))
  const feeTotals = sortedKeys.map(k => feeBuckets.get(k) || 0)
  const totalOrders = orderCounts.reduce((s, v) => s + v, 0)
  const totalFees = feeTotals.reduce((s, v) => s + v, 0)

  const colKey = i => (labels[i] || '').toLowerCase().replace(/\s/g, '_').replace(/,/g, '') || `col_${i}`
  const columns = labels.map((l, i) => ({ key: colKey(i), label: l || `Period ${i + 1}` }))

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Orders',
        data: orderCounts,
        borderColor: 'rgb(71, 114, 196)',
        backgroundColor: 'rgba(71, 114, 196, 0.08)',
        yAxisID: 'y',
        tension: 0.4,
        fill: true,
        borderWidth: 1.5
      },
      {
        label: 'Recurring fee',
        data: feeTotals,
        borderColor: 'rgb(75, 163, 120)',
        backgroundColor: 'rgba(75, 163, 120, 0.08)',
        yAxisID: 'y1',
        tension: 0.4,
        fill: true,
        borderWidth: 1.5
      }
    ]
  }

  const formatCurrency = v =>
    v == null || Number(v) === 0
      ? '—'
      : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  const formatCurrencyTotal = v =>
    v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  const orderRowValues = {}
  const feeRowValues = {}
  columns.forEach((col, i) => {
    orderRowValues[col.key] = (orderCounts[i] ?? 0) === 0 ? '—' : String(orderCounts[i])
    feeRowValues[col.key] = formatCurrency(feeTotals[i])
  })

  const tableData = {
    columns,
    rows: [
      {
        label: 'Orders',
        color: 'rgb(71, 114, 196)',
        values: orderRowValues,
        total: String(totalOrders)
      },
      {
        label: 'Recurring fee',
        color: 'rgb(75, 163, 120)',
        values: feeRowValues,
        total: formatCurrencyTotal(totalFees)
      }
    ]
  }

  return { chartData, tableData }
}

export const PRODUCT_COLORS = [
  'rgb(71, 114, 196)',
  'rgb(75, 163, 120)',
  'rgb(196, 170, 80)',
  'rgb(205, 92, 92)',
  'rgb(140, 110, 180)',
  'rgb(190, 110, 140)',
  'rgb(80, 158, 150)',
  'rgb(200, 130, 80)',
  'rgb(100, 140, 170)',
  'rgb(160, 130, 100)',
  'rgb(120, 165, 95)',
  'rgb(175, 100, 130)',
  'rgb(95, 130, 185)',
  'rgb(180, 150, 110)',
  'rgb(110, 150, 160)',
  'rgb(165, 120, 155)'
]

/**
 * Parses orders report API response into chart + table + tooltip data for ReportChart.
 * Accepts JSONAPI shape (data.data.attributes) or flat shape (data.data / data).
 * @param {Object} apiResponse - { data: { data: { attributes: { periods, ... } } } } or { data: { data: { periods, ... } } } or { data: { periods, ... } }
 * @returns {{ chartData: Object, tableData: Object, tooltipData: Object }}
 */
export function parseOrdersReport(apiResponse) {
  const payload = apiResponse?.data?.data
  const data = payload?.attributes ?? payload ?? apiResponse?.data ?? {}
  const periods = data.periods ?? []
  const table = data.table ?? {}
  const tableRows = table.rows ?? []
  const fulfillmentMethodOptions = data.fulfillment_method_options ?? []

  if (!periods.length) {
    return {
      chartData: { labels: [], datasets: [] },
      tableData: { columns: [{ key: 'count', label: 'Count' }], rows: [] },
      tooltipData: { periodBreakdown: [] }
    }
  }

  const labels = periods.map(p => p.period_label)
  const fulfillmentMethodIds = [
    ...new Set(periods.flatMap(p => (p.fulfillment_methods || []).map(fm => fm.fulfillment_method_id)))
  ].sort((a, b) => a - b)
  const colorByFmId = Object.fromEntries(
    fulfillmentMethodIds.map((id, i) => [id, PRODUCT_COLORS[i % PRODUCT_COLORS.length]])
  )
  const fmIdToName = {}
  tableRows.forEach(r => {
    if (r.fulfillment_method_id != null && r.name != null) fmIdToName[r.fulfillment_method_id] = r.name
  })
  fulfillmentMethodOptions.forEach(fm => {
    if (fm.id != null && fm.name != null) fmIdToName[fm.id] = fm.name
  })

  const datasets = fulfillmentMethodIds.map(fmId => {
    const name = fmIdToName[fmId] ?? `Fulfillment ${fmId}`
    const data = periods.map(period => {
      const fm = (period.fulfillment_methods || []).find(f => f.fulfillment_method_id === fmId)
      return fm?.order_count ?? 0
    })
    return {
      fulfillmentMethodId: fmId,
      label: name,
      data,
      backgroundColor: colorByFmId[fmId],
      borderColor: colorByFmId[fmId],
      yAxisID: 'y'
    }
  })

  const chartData = { labels, datasets }

  const periodBreakdown = periods.map(p => ({
    period_label: p.period_label,
    fulfillment_methods: (p.fulfillment_methods || []).map(fm => ({
      fulfillment_method_id: fm.fulfillment_method_id,
      name: fm.name,
      order_count: fm.order_count ?? 0
    }))
  }))
  const tooltipData = { periodBreakdown }

  const colKey = i => (labels[i] || '').toLowerCase().replace(/\s/g, '_').replace(/,/g, '') || `col_${i}`
  const columns = labels.map((l, i) => ({ key: colKey(i), label: l || `Period ${i + 1}` }))
  // Filter out fulfillment methods with zero counts (excluded by API filter)
  const nonZeroTableRows = tableRows.filter(row => (row.count ?? 0) > 0)
  const nonZeroFmIds = new Set(nonZeroTableRows.map(r => r.fulfillment_method_id))

  const tableData = {
    columns,
    rows: nonZeroTableRows.map(row => {
      const periodCounts = row.period_counts ?? []
      const values = {}
      columns.forEach((col, i) => {
        const count = periodCounts[i] ?? 0
        values[col.key] = String(count)
      })
      return {
        fulfillmentMethodId: row.fulfillment_method_id,
        label: row.name ?? '',
        color: colorByFmId[row.fulfillment_method_id] ?? '#6b7280',
        values,
        total: String(row.count ?? 0)
      }
    })
  }

  // Filter chart datasets to match visible table rows
  chartData.datasets = chartData.datasets.filter(ds => nonZeroFmIds.has(ds.fulfillmentMethodId))

  return { chartData, tableData, tooltipData }
}

/**
 * Parses products sold breakdown API response into chart + table + tooltip data for ReportChart.
 * Accepts either the full API response (res) or the response body (res.data).
 * @param {Object} apiResponse - Full response: { data: { data: { periods, products_options, table } } }; or body: { data: { periods, products_options, table } }
 * @param {Object} [options] - Optional: { limitToTopProducts: number } to restrict to top N products by revenue
 * @returns {{ chartData: Object, tableData: Object, tooltipData: Object, productsOptions: Array }}
 */
export function parseProductsSoldBreakdownReport(apiResponse, options) {
  const data = apiResponse?.data?.data ?? apiResponse?.data ?? {}
  const periods = data.periods ?? []
  const table = data.table ?? {}
  const tableRows = table.rows ?? []
  const productsOptions = data.products_options ?? []

  if (!periods.length) {
    return {
      chartData: { labels: [], datasets: [] },
      tableData: {
        columns: [
          { key: 'count', label: 'Count', format: 'number' },
          { key: 'average_price', label: 'Average price', format: 'currency' }
        ],
        rows: []
      },
      tooltipData: { periodBreakdown: [] },
      productsOptions: []
    }
  }

  const limitToTopProducts = options?.limitToTopProducts
  const sortedByRevenue = [...tableRows].sort((a, b) => (b.revenue_cents ?? 0) - (a.revenue_cents ?? 0))
  const effectiveTableRows =
    limitToTopProducts != null && limitToTopProducts > 0
      ? sortedByRevenue.slice(0, limitToTopProducts)
      : sortedByRevenue
  const productIds =
    limitToTopProducts != null && limitToTopProducts > 0
      ? [...new Set(effectiveTableRows.map(r => r.product_id).filter(id => id != null))].sort((a, b) => a - b)
      : [...new Set(periods.flatMap(p => (p.products || []).map(x => x.product_id)))].sort((a, b) => a - b)

  const labels = periods.map(p => p.period_label)
  const colorByProductId = Object.fromEntries(
    productIds.map((id, i) => [id, PRODUCT_COLORS[i % PRODUCT_COLORS.length]])
  )

  const productIdToName = {}
  tableRows.forEach(r => {
    if (r.product_id != null && r.product_name != null) productIdToName[r.product_id] = r.product_name
  })
  productsOptions.forEach(p => {
    if (p.id != null && p.product_name != null) productIdToName[p.id] = p.product_name
  })

  const productIdSet = new Set(productIds)
  const datasets = productIds
    .map(productId => {
      const productName =
        productIdToName[productId] ??
        periods[0].products?.find(p => p.product_id === productId)?.product_name ??
        periods.flatMap(p => p.products || []).find(p => p.product_id === productId)?.product_name ??
        `Product ${productId}`
      const data = periods.map(period => {
        const item = (period.products || []).find(p => p.product_id === productId)
        const revenueCents = item?.revenue_cents ?? 0
        return Number((revenueCents / 100).toFixed(2))
      })
      return {
        productId,
        label: productName,
        data,
        backgroundColor: colorByProductId[productId],
        borderColor: colorByProductId[productId],
        yAxisID: 'y'
      }
    })
    .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '', undefined, { sensitivity: 'base' }))

  const chartData = { labels, datasets }

  const periodBreakdown = periods.map(p => ({
    period_label: p.period_label,
    products: (p.products || [])
      .filter(x => productIdSet.has(x.product_id))
      .map(x => ({
        product_id: x.product_id,
        product_name: x.product_name,
        quantity: x.quantity,
        revenue_cents: x.revenue_cents
      }))
  }))
  const tooltipData = { periodBreakdown }

  const formatCurrency = v =>
    v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  const formatCurrencyTotal = v =>
    v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  const sortedTableRows = effectiveTableRows
  const tableData = {
    columns: [
      { key: 'count', label: 'Count', format: 'number' },
      { key: 'average_price', label: 'Average price', format: 'currency' }
    ],
    rows: sortedTableRows.map(row => {
      const count = Number(row.count ?? 0) || 1
      const revenueCents = Number(row.revenue_cents ?? 0)
      const avgPriceCents = count > 0 ? revenueCents / count : 0
      return {
        productId: row.product_id,
        label: row.product_name ?? '',
        color: colorByProductId[row.product_id] ?? '#6b7280',
        values: {
          count: Number(row.count ?? 0).toLocaleString(),
          average_price: formatCurrency(avgPriceCents / 100)
        },
        total: formatCurrencyTotal(revenueCents / 100)
      }
    })
  }

  const sortedProductsOptions = [...productsOptions].sort((a, b) =>
    (a.product_name ?? '').localeCompare(b.product_name ?? '', undefined, { sensitivity: 'base' })
  )
  const displayedProductIds = limitToTopProducts != null && limitToTopProducts > 0 ? productIds : undefined
  return {
    chartData,
    tableData,
    tooltipData,
    productsOptions: sortedProductsOptions,
    ...(displayedProductIds != null ? { displayedProductIds } : {})
  }
}

/**
 * Parses discount code usage report API response into chart + table + tooltip data for ReportChart.
 * @param {Object} apiResponse - { data: { data: { periods, discount_code_options, table } } }
 * @returns {{ chartData: Object, tableData: Object, tooltipData: Object, productsOptions: Array }}
 */
export function parseDiscountCodeUsageReport(apiResponse) {
  const payload = apiResponse?.data?.data
  const data = payload?.attributes ?? payload ?? apiResponse?.data ?? {}
  const periods = data.periods ?? []
  const table = data.table ?? {}
  const tableRows = table.rows ?? []
  const discountCodeOptions = data.discount_code_options ?? []

  const columns = [
    { key: 'type', label: 'Type', format: 'string' },
    { key: 'usage_count', label: 'Uses', format: 'number' }
  ]

  if (!periods.length) {
    return {
      chartData: { labels: [], datasets: [] },
      tableData: { columns, rows: [] },
      tooltipData: { periodBreakdown: [] },
      productsOptions: []
    }
  }

  const codeIds = [...new Set(periods.flatMap(p => (p.discount_codes || []).map(dc => dc.discount_code_id)))].sort(
    (a, b) => a - b
  )

  const labels = periods.map(p => p.period_label)
  const colorByCodeId = Object.fromEntries(codeIds.map((id, i) => [id, PRODUCT_COLORS[i % PRODUCT_COLORS.length]]))

  const codeIdToName = {}
  const codeIdToType = {}
  tableRows.forEach(r => {
    if (r.discount_code_id != null) {
      codeIdToName[r.discount_code_id] = r.code
      codeIdToType[r.discount_code_id] = r.type
    }
  })
  discountCodeOptions.forEach(dc => {
    if (dc.id != null) {
      codeIdToName[dc.id] = dc.code
      codeIdToType[dc.id] = dc.type
    }
  })

  const datasets = codeIds
    .map(codeId => {
      const name = codeIdToName[codeId] ?? `Code ${codeId}`
      const chartValues = periods.map(period => {
        const dc = (period.discount_codes || []).find(d => d.discount_code_id === codeId)
        return Number(((dc?.discount_cents ?? 0) / 100).toFixed(2))
      })
      return {
        productId: codeId,
        label: name,
        data: chartValues,
        backgroundColor: colorByCodeId[codeId],
        borderColor: colorByCodeId[codeId],
        yAxisID: 'y'
      }
    })
    .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '', undefined, { sensitivity: 'base' }))

  const chartData = { labels, datasets }

  const codeIdSet = new Set(codeIds)
  const periodBreakdown = periods.map(p => ({
    period_label: p.period_label,
    products: (p.discount_codes || [])
      .filter(dc => codeIdSet.has(dc.discount_code_id))
      .map(dc => ({
        product_id: dc.discount_code_id,
        product_name: codeIdToName[dc.discount_code_id] ?? dc.code,
        quantity: dc.usage_count,
        revenue_cents: dc.discount_cents
      }))
  }))
  const tooltipData = { periodBreakdown }

  const formatCurrency = v =>
    v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  const typeLabel = type => {
    if (!type) return ''
    if (type.includes('PercentOff')) return '% off'
    if (type.includes('AmountOff')) return '$ off'
    if (type.includes('FreeShipping')) return 'Free shipping'
    return type
  }

  const sortedTableRows = [...tableRows].sort((a, b) => (b.discount_cents ?? 0) - (a.discount_cents ?? 0))
  const tableData = {
    columns,
    rows: sortedTableRows.map(row => ({
      productId: row.discount_code_id,
      label: row.code ?? '',
      color: colorByCodeId[row.discount_code_id] ?? '#6b7280',
      values: {
        type: typeLabel(row.type),
        usage_count: Number(row.usage_count ?? 0).toLocaleString()
      },
      total: formatCurrency((row.discount_cents ?? 0) / 100)
    }))
  }

  const sortedOptions = [...discountCodeOptions].sort((a, b) =>
    (a.code ?? '').localeCompare(b.code ?? '', undefined, { sensitivity: 'base' })
  )
  const productsOptions = sortedOptions.map(dc => ({
    id: dc.id,
    product_name: dc.code
  }))

  return { chartData, tableData, tooltipData, productsOptions }
}

/**
 * Parses referral code usage report API response into chart + table + tooltip data for ReportChart.
 * @param {Object} apiResponse - { data: { data: { periods, referral_code_options, table } } }
 * @returns {{ chartData: Object, tableData: Object, tooltipData: Object, productsOptions: Array }}
 */
export function parseReferralCodeUsageReport(apiResponse, { useCustomerLabel = false } = {}) {
  const payload = apiResponse?.data?.data
  const data = payload?.attributes ?? payload ?? apiResponse?.data ?? {}
  const periods = data.periods ?? []
  const table = data.table ?? {}
  const tableRows = table.rows ?? []
  const referralCodeOptions = data.referral_code_options ?? []

  const columns = useCustomerLabel
    ? [
        { key: 'referral_code', label: 'Referral Code', format: 'string' },
        { key: 'type', label: 'Type', format: 'string' },
        { key: 'usage_count', label: 'Uses', format: 'number' }
      ]
    : [
        { key: 'type', label: 'Type', format: 'string' },
        { key: 'usage_count', label: 'Uses', format: 'number' }
      ]

  if (!periods.length) {
    return {
      chartData: { labels: [], datasets: [] },
      tableData: { columns, rows: [] },
      tooltipData: { periodBreakdown: [] },
      productsOptions: []
    }
  }

  const codeIds = [...new Set(periods.flatMap(p => (p.referral_codes || []).map(dc => dc.discount_code_id)))].sort(
    (a, b) => a - b
  )

  const labels = periods.map(p => p.period_label)
  const colorByCodeId = Object.fromEntries(codeIds.map((id, i) => [id, PRODUCT_COLORS[i % PRODUCT_COLORS.length]]))

  const codeIdToName = {}
  const codeIdToType = {}
  tableRows.forEach(r => {
    if (r.discount_code_id != null) {
      codeIdToName[r.discount_code_id] = r.code
      codeIdToType[r.discount_code_id] = r.type
    }
  })
  referralCodeOptions.forEach(dc => {
    if (dc.id != null) {
      codeIdToName[dc.id] = dc.code
      codeIdToType[dc.id] = dc.type
    }
  })

  const datasets = codeIds
    .map(codeId => {
      const name = codeIdToName[codeId] ?? `Code ${codeId}`
      const chartValues = periods.map(period => {
        const dc = (period.referral_codes || []).find(d => d.discount_code_id === codeId)
        return Number(((dc?.discount_cents ?? 0) / 100).toFixed(2))
      })
      return {
        productId: codeId,
        label: name,
        data: chartValues,
        backgroundColor: colorByCodeId[codeId],
        borderColor: colorByCodeId[codeId],
        yAxisID: 'y'
      }
    })
    .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '', undefined, { sensitivity: 'base' }))

  const chartData = { labels, datasets }

  const codeIdSet = new Set(codeIds)
  const periodBreakdown = periods.map(p => ({
    period_label: p.period_label,
    products: (p.referral_codes || [])
      .filter(dc => codeIdSet.has(dc.discount_code_id))
      .map(dc => ({
        product_id: dc.discount_code_id,
        product_name: codeIdToName[dc.discount_code_id] ?? dc.code,
        quantity: dc.usage_count,
        revenue_cents: dc.discount_cents
      }))
  }))
  const tooltipData = { periodBreakdown }

  const formatCurrency = v =>
    v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  const typeLabel = type => {
    if (!type) return ''
    if (type.includes('PercentOff')) return '% off'
    if (type.includes('AmountOff')) return '$ off'
    if (type.includes('FreeShipping')) return 'Free shipping'
    return type
  }

  const sortedTableRows = [...tableRows].sort((a, b) => (b.discount_cents ?? 0) - (a.discount_cents ?? 0))
  const tableData = {
    columns,
    rows: sortedTableRows.map(row => ({
      productId: row.discount_code_id,
      label: useCustomerLabel ? row.customer_name ?? '—' : row.code ?? '',
      color: colorByCodeId[row.discount_code_id] ?? '#6b7280',
      values: {
        ...(useCustomerLabel ? { referral_code: row.code ?? '' } : {}),
        type: typeLabel(row.type),
        usage_count: Number(row.usage_count ?? 0).toLocaleString()
      },
      total: formatCurrency((row.discount_cents ?? 0) / 100)
    }))
  }

  const sortedOptions = [...referralCodeOptions].sort((a, b) =>
    (a.code ?? '').localeCompare(b.code ?? '', undefined, { sensitivity: 'base' })
  )
  const productsOptions = sortedOptions.map(dc => ({
    id: dc.id,
    product_name: dc.code
  }))

  return { chartData, tableData, tooltipData, productsOptions }
}

/**
 * Parses customer growth by tier report API response into chart + table + tooltip data for ReportChart.
 * @param {Object} apiResponse - { data: { data: { periods, membership_tier_options, table } } }
 * @returns {{ chartData: Object, tableData: Object, tooltipData: Object, productsOptions: Array }}
 */
export function parseCustomerGrowthByTierReport(apiResponse) {
  const payload = apiResponse?.data?.data
  const data = payload?.attributes ?? payload ?? apiResponse?.data ?? {}
  const periods = data.periods ?? []
  const table = data.table ?? {}
  const tableRows = table.rows ?? []
  const membershipTierOptions = data.membership_tier_options ?? []

  if (!periods.length) {
    return {
      chartData: { labels: [], datasets: [] },
      tableData: { columns: [], rows: [] }
    }
  }

  const tierIds = [...new Set(periods.flatMap(p => (p.membership_tiers || []).map(t => t.membership_tier_id)))].sort(
    (a, b) => a - b
  )

  const labels = periods.map(p => p.period_label)
  const colorByTierId = Object.fromEntries(tierIds.map((id, i) => [id, PRODUCT_COLORS[i % PRODUCT_COLORS.length]]))

  const tierIdToName = {}
  tableRows.forEach(r => {
    if (r.membership_tier_id != null) tierIdToName[r.membership_tier_id] = r.tier_name
  })
  membershipTierOptions.forEach(t => {
    if (t.id != null) tierIdToName[t.id] = t.tier_name
  })

  const datasets = tierIds
    .map(tierId => {
      const name = tierIdToName[tierId] ?? `Tier ${tierId}`
      const color = colorByTierId[tierId]
      const chartValues = periods.map(period => {
        const tier = (period.membership_tiers || []).find(t => t.membership_tier_id === tierId)
        return tier?.customer_count ?? 0
      })
      return {
        label: name,
        data: chartValues,
        borderColor: color,
        backgroundColor: color ? `${color.replace('rgb(', 'rgba(').replace(')', ', 0.08)')}` : 'rgba(0,0,0,0.08)',
        yAxisID: 'y',
        tension: 0.4,
        fill: true,
        borderWidth: 1.5
      }
    })
    .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '', undefined, { sensitivity: 'base' }))

  const chartData = { labels, datasets }

  const colKey = i => (labels[i] || '').toLowerCase().replace(/\s/g, '_').replace(/,/g, '') || `col_${i}`
  const columns = labels.map((l, i) => ({ key: colKey(i), label: l || `Period ${i + 1}` }))

  const sortedTableRows = [...tableRows].sort((a, b) => (b.customer_count ?? 0) - (a.customer_count ?? 0))
  const tableData = {
    columns,
    rows: sortedTableRows.map(row => {
      const tierId = row.membership_tier_id
      const tierPeriodCounts = periods.map(p => {
        const tier = (p.membership_tiers || []).find(t => t.membership_tier_id === tierId)
        return tier?.customer_count ?? 0
      })
      const values = {}
      columns.forEach((col, i) => {
        const count = tierPeriodCounts[i] ?? 0
        values[col.key] = count === 0 ? '—' : String(count)
      })
      return {
        label: row.tier_name ?? '',
        color: colorByTierId[tierId] ?? '#6b7280',
        values,
        total: String(row.customer_count ?? 0)
      }
    })
  }

  return { chartData, tableData }
}

/**
 * Parses membership tier growth report API response into chart + table data for ReportChart.
 * Identical shape to customer growth by tier; reused parser.
 * @param {Object} apiResponse
 * @returns {{ chartData: Object, tableData: Object }}
 */
export function parseMembershipTierGrowthReport(apiResponse) {
  return parseCustomerGrowthByTierReport(apiResponse)
}

/**
 * Parses revenue by membership tier report API response into chart + table data for ReportChart.
 * @param {Object} apiResponse - { data: { data: { periods, membership_tier_options, table } } }
 * @returns {{ chartData: Object, tableData: Object }}
 */
export function parseRevenueByMembershipTierReport(apiResponse) {
  const payload = apiResponse?.data?.data
  const data = payload?.attributes ?? payload ?? apiResponse?.data ?? {}
  const periods = data.periods ?? []
  const table = data.table ?? {}
  const tableRows = table.rows ?? []
  const membershipTierOptions = data.membership_tier_options ?? []

  if (!periods.length) {
    return {
      chartData: { labels: [], datasets: [] },
      tableData: { columns: [], rows: [] }
    }
  }

  const tierIds = [...new Set(periods.flatMap(p => (p.membership_tiers || []).map(t => t.membership_tier_id)))].sort(
    (a, b) => a - b
  )

  const labels = periods.map(p => p.period_label)
  const colorByTierId = Object.fromEntries(tierIds.map((id, i) => [id, PRODUCT_COLORS[i % PRODUCT_COLORS.length]]))

  const tierIdToName = {}
  tableRows.forEach(r => {
    if (r.membership_tier_id != null) tierIdToName[r.membership_tier_id] = r.tier_name
  })
  membershipTierOptions.forEach(t => {
    if (t.id != null) tierIdToName[t.id] = t.tier_name
  })

  const datasets = tierIds
    .map(tierId => {
      const name = tierIdToName[tierId] ?? `Tier ${tierId}`
      const color = colorByTierId[tierId]
      const chartValues = periods.map(period => {
        const tier = (period.membership_tiers || []).find(t => t.membership_tier_id === tierId)
        return (tier?.revenue_cents ?? 0) / 100
      })
      return {
        label: name,
        data: chartValues,
        borderColor: color,
        backgroundColor: color ? `${color.replace('rgb(', 'rgba(').replace(')', ', 0.08)')}` : 'rgba(0,0,0,0.08)',
        yAxisID: 'y',
        tension: 0.4,
        fill: true,
        borderWidth: 1.5
      }
    })
    .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '', undefined, { sensitivity: 'base' }))

  const chartData = { labels, datasets }

  const formatCurrency = v =>
    v == null || Number(v) === 0
      ? '—'
      : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  const formatCurrencyTotal = v =>
    v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  const colKey = i => (labels[i] || '').toLowerCase().replace(/\s/g, '_').replace(/,/g, '') || `col_${i}`
  const columns = labels.map((l, i) => ({ key: colKey(i), label: l || `Period ${i + 1}` }))

  const sortedTableRows = [...tableRows].sort((a, b) => (b.revenue_cents ?? 0) - (a.revenue_cents ?? 0))
  const tableData = {
    columns,
    rows: sortedTableRows.map(row => {
      const tierId = row.membership_tier_id
      const tierPeriodRevenues = periods.map(p => {
        const tier = (p.membership_tiers || []).find(t => t.membership_tier_id === tierId)
        return (tier?.revenue_cents ?? 0) / 100
      })
      const values = {}
      columns.forEach((col, i) => {
        values[col.key] = formatCurrency(tierPeriodRevenues[i])
      })
      return {
        label: row.tier_name ?? '',
        color: colorByTierId[tierId] ?? '#6b7280',
        values,
        total: formatCurrencyTotal((row.revenue_cents ?? 0) / 100)
      }
    })
  }

  return { chartData, tableData }
}

export function parseMealPlansReport(data, groupBy = 'day') {
  const payments = data?.payments ?? []
  const newSubscriptions = data?.new_subscriptions ?? []
  const cancellations = data?.cancellations ?? []
  const redemptions = data?.redemptions ?? []
  const activeSubscribers = data?.active_subscribers ?? 0

  if (
    payments.length === 0 &&
    newSubscriptions.length === 0 &&
    cancellations.length === 0 &&
    redemptions.length === 0
  ) {
    return {
      chartData: { labels: [], datasets: [] },
      tableData: {
        columns: [{ key: 'now', label: 'Current' }],
        rows: [{ label: 'Active subscribers', color: 'rgb(71, 114, 196)', values: { now: String(activeSubscribers) } }]
      }
    }
  }

  const getPeriodKey = ts => {
    const d = new Date(ts * 1000)
    const y = d.getFullYear()
    const m = d.getMonth()
    const date = d.getDate()
    if (groupBy === 'year') return `${y}`
    if (groupBy === 'month') return `${y}-${String(m + 1).padStart(2, '0')}`
    if (groupBy === 'week') {
      const day = d.getDay()
      const start = new Date(d)
      start.setDate(date - day)
      return `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`
    }
    return `${y}-${String(m + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`
  }

  const periodToLabel = key => {
    if (groupBy === 'year') return key
    const parts = key.split('-').map(Number)
    const y = parts[0]
    const m = parts[1]
    const d = parts[2] ?? 1
    if (groupBy === 'month') {
      return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    }
    if (groupBy === 'week') {
      const date = new Date(y, m - 1, d)
      return `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    }
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const revenueBuckets = new Map()
  const paymentCountBuckets = new Map()
  const redemptionBuckets = new Map()
  const newSubBuckets = new Map()
  const cancelBuckets = new Map()
  const touch = key => {
    if (!revenueBuckets.has(key)) {
      revenueBuckets.set(key, 0)
      paymentCountBuckets.set(key, 0)
      redemptionBuckets.set(key, 0)
      newSubBuckets.set(key, 0)
      cancelBuckets.set(key, 0)
    }
  }
  for (const row of payments) {
    const key = getPeriodKey(row.date)
    touch(key)
    if (!row.is_refunded) revenueBuckets.set(key, revenueBuckets.get(key) + (row.amount_cents || 0) / 100)
    paymentCountBuckets.set(key, paymentCountBuckets.get(key) + 1)
  }
  for (const row of redemptions) {
    const key = getPeriodKey(row.date)
    touch(key)
    redemptionBuckets.set(key, redemptionBuckets.get(key) + (row.units || 0))
  }
  for (const row of newSubscriptions) {
    const key = getPeriodKey(row.date)
    touch(key)
    newSubBuckets.set(key, newSubBuckets.get(key) + 1)
  }
  for (const row of cancellations) {
    const key = getPeriodKey(row.date)
    touch(key)
    cancelBuckets.set(key, cancelBuckets.get(key) + 1)
  }

  const sortedKeys = [...revenueBuckets.keys()].sort()
  const labels = sortedKeys.map(periodToLabel)
  const revenue = sortedKeys.map(k => revenueBuckets.get(k))
  const redemptionCounts = sortedKeys.map(k => redemptionBuckets.get(k))

  const colKey = i => (labels[i] || '').toLowerCase().replace(/\s/g, '_').replace(/,/g, '') || `col_${i}`
  const columns = labels.map((l, i) => ({ key: colKey(i), label: l || `Period ${i + 1}` }))

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Meals redeemed',
        data: redemptionCounts,
        borderColor: 'rgb(71, 114, 196)',
        backgroundColor: 'rgba(71, 114, 196, 0.08)',
        yAxisID: 'y',
        tension: 0.4,
        fill: true,
        borderWidth: 1.5
      },
      {
        label: 'Plan revenue',
        data: revenue,
        borderColor: 'rgb(75, 163, 120)',
        backgroundColor: 'rgba(75, 163, 120, 0.08)',
        yAxisID: 'y1',
        tension: 0.4,
        fill: true,
        borderWidth: 1.5
      }
    ]
  }

  const formatCurrency = v =>
    v == null || Number(v) === 0
      ? '-'
      : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  const countCell = v => ((v ?? 0) === 0 ? '-' : String(v))
  const rowFor = (label, color, buckets, formatter) => {
    const values = {}
    columns.forEach((col, i) => {
      values[col.key] = formatter(buckets.get(sortedKeys[i]))
    })
    return { label, color, values }
  }

  const tableData = {
    columns,
    rows: [
      rowFor('Plan revenue', 'rgb(75, 163, 120)', revenueBuckets, formatCurrency),
      rowFor('Payments', 'rgb(75, 163, 120)', paymentCountBuckets, countCell),
      rowFor('Meals redeemed', 'rgb(71, 114, 196)', redemptionBuckets, countCell),
      rowFor('New subscribers', 'rgb(140, 110, 190)', newSubBuckets, countCell),
      rowFor('Cancellations', 'rgb(200, 90, 90)', cancelBuckets, countCell),
      (() => {
        const values = {}
        columns.forEach((col, i) => {
          values[col.key] = i === columns.length - 1 ? String(activeSubscribers) : '-'
        })
        return { label: 'Active subscribers (now)', color: 'rgb(71, 114, 196)', values }
      })()
    ]
  }

  return { chartData, tableData }
}
